<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'
import {
  LandsidePageTab,
  SubscriptionFilter,
  buildLandsidePagePath,
  buildSubscriptionPagePath,
} from '@@/bits/landside_helper'
import { buildUrlFromPath } from '@@/bits/location'
import { getTaUrlWithUtmParams } from '@@/bits/ta'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPadletLogo, {
  OzPadletLogoColorScheme,
  OzPadletLogoSizePreset,
} from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { AccessibilitySetting, useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { LandsidePage, useLandsideNavigationStore } from '@@/pinia/landside_navigation'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { VueCssClass } from '@@/types'
import LandsideCallToAction from '@@/vuecomponents/LandsideCallToAction.vue'
import type { LandsideNavigation } from '@@/vuecomponents/LandsideFooterSection.vue'
import LandsideFooterSection from '@@/vuecomponents/LandsideFooterSection.vue'
import LandsideHeaderFooterBorder from '@@/vuecomponents/LandsideHeaderFooterBorder.vue'
import LandsideHomepageFooterIllustration from '@@/vuecomponents/LandsideHomepageFooterIllustration.vue'
import LocaleSelector from '@@/vuecomponents/LocaleSelector.vue'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref } from 'vue'

const AccessibilitySettingsModal = defineAsyncComponent(() => import('@@/vuecomponents/AccessibilitySettingsModal.vue'))

const { isSmallerThanTabletLandscape, windowWidth } = storeToRefs(useWindowSizeStore())
const { isDarkMode } = storeToRefs(useDarkModeStore())
const { currentPage, landsidePages, isUserLoggedIn, currentUser } = storeToRefs(useLandsideNavigationStore())
const accessibilitySettingsStore = useAccessibilitySettingsStore()

const socialMediaIcons = [
  {
    icon: 'x_logo_filled',
    url: 'https://twitter.com/padlet',
    label: __('Twitter'),
    testId: 'socialMediaButtonTwitter',
    size: 18,
  },
  {
    icon: 'youtube_filled',
    url: 'https://www.youtube.com/@padlet',
    label: __('Youtube'),
    testId: 'socialMediaButtonYoutube',
    size: 24,
  },
  {
    icon: 'instagram_filled',
    url: 'https://www.instagram.com/padlet',
    label: __('Instagram'),
    testId: 'socialMediaButtonInstagram',
    size: 19,
  },
  {
    icon: 'facebook_filled',
    url: 'https://www.facebook.com/padlet/',
    label: __('Facebook'),
    testId: 'socialMediaButtonFacebook',
    size: 24,
  },
  {
    icon: 'linkedin_filled',
    url: 'https://www.linkedin.com/company/padlet',
    label: __('Linkedin'),
    testId: 'socialMediaButtonLinkedin',
    size: 18,
  },
]

const productNavigationItems: LandsideNavigation[] = [
  { name: __('Board'), url: buildUrlFromPath(buildLandsidePagePath(LandsidePageTab.Board)) },
  { name: __('Sandbox'), url: buildUrlFromPath(buildLandsidePagePath(LandsidePageTab.Sandbox)) },
  {
    name: __('Arcade'),
    url: getTaUrlWithUtmParams('home_footer', null, isUserLoggedIn.value),
    target: '_blank',
    iconName: 'external_link',
  },
  { name: __('Pricing'), url: buildUrlFromPath(buildSubscriptionPagePath(SubscriptionFilter.ALL)) },
  { name: __('Apps and integrations'), url: 'https://www.padlet.com/about/apps' },
]

const solutionsNavigationItems: LandsideNavigation[] = [
  { name: __('Templates'), url: buildUrlFromPath(buildLandsidePagePath(LandsidePageTab.Templates)) },
  { name: __('Education'), url: buildUrlFromPath(buildLandsidePagePath(LandsidePageTab.Education)) },
]

const supportNavigationItems: LandsideNavigation[] = [
  { name: __('Knowledge base'), url: 'https://www.padlet.help' },
  { name: __('Video tutorials'), url: 'https://www.youtube.com/padlet' },
  { name: __('Developer docs'), url: 'https://docs.padlet.dev/' },
  { name: __('Contact us'), url: 'https://www.padlet.com/contact-us' },
]

const companyNavigationItems: LandsideNavigation[] = [
  { name: __('About us'), url: 'https://padlet.jobs' },
  { name: __('Jobs'), url: 'https://padlet.jobs/open-roles' },
  { name: __('Blog'), url: 'https://padlet.blog/' },
]

const legalNavigationItems: LandsideNavigation[] = [
  { name: __('Terms of use'), url: 'https://legal.padlet.com/terms' },
  { name: __('Privacy policy'), url: 'https://legal.padlet.com/privacy' },
  { name: __('Content policy'), url: 'https://legal.padlet.com/content' },
  { name: __('Accessibility statement'), url: 'https://legal.padlet.com/accessibility' },
]

const footerIllustrationDarkUrl = 'https://padlet.net/homepage/v4/footer_illustration_dark.webp'
const footerIllustrationLightUrl = 'https://padlet.net/homepage/v4/footer_illustration_light.webp'
const ILLUSTRATION_ORIGINAL_IMAGE_WIDTH = 11520
const ILLUSTRATION_ORIGINAL_IMAGE_HEIGHT = 1152
const illustrationContainer = ref<HTMLDivElement>()

// We use maxAvailableWidth to handle the case when users zoom in/out which causes
// the windowWidth to change
const maxAvailableWidth = computed((): number => {
  if (!illustrationContainer.value) return windowWidth.value
  return Math.max(illustrationContainer.value.clientWidth, windowWidth.value)
})

const illustrationHeight = computed((): number => {
  if (isSmallerThanTabletLandscape.value) return 80
  return 138
})

const illustrationWidth = computed((): number => {
  // We calculate the width of the illustration using the ratio (width / height) of the original image
  return illustrationHeight.value * (ILLUSTRATION_ORIGINAL_IMAGE_WIDTH / ILLUSTRATION_ORIGINAL_IMAGE_HEIGHT)
})

const numberOfIllustrationsDisplayed = computed((): number => {
  if (maxAvailableWidth.value < illustrationWidth.value) return 1
  return Math.ceil(maxAvailableWidth.value / illustrationWidth.value)
})

const lastIllustrationWidth = computed((): number => {
  const remainingWidth = maxAvailableWidth.value - (numberOfIllustrationsDisplayed.value - 1) * illustrationWidth.value
  return remainingWidth === 0 ? illustrationWidth.value : remainingWidth
})

const isHomePage = computed((): boolean => {
  return currentPage.value === LandsidePage.Home
})

const isBoardPage = computed((): boolean => {
  return currentPage.value === LandsidePage.Board
})

const isSandboxPage = computed((): boolean => {
  return currentPage.value === LandsidePage.Sandbox
})

const isEducationPage = computed((): boolean => {
  return currentPage.value === LandsidePage.Education
})

const xCustomFooterBorder = computed((): boolean => {
  return (
    isBoardPage.value ||
    currentPage.value === LandsidePage.Subscriptions ||
    currentPage.value === LandsidePage.Templates ||
    isEducationPage.value ||
    isSandboxPage.value
  )
})

const useCustomTextColor = computed(() => {
  return isEducationPage.value || isBoardPage.value || isSandboxPage.value
})

const customTextColorClasses = computed<VueCssClass | undefined>(() => {
  if (isDarkMode.value) return 'text-light-text-200'

  switch (currentPage.value) {
    case LandsidePage.Education:
      return 'text-[rgba(3,51,0,0.54)] high-contrast:text-dark-text-100'
    case LandsidePage.Board:
      return 'text-[rgba(28,0,51,0.54)] high-contrast:text-dark-text-100'
    case LandsidePage.Sandbox:
      return 'text-[rgba(51,39,0,0.54)] high-contrast:text-dark-text-100'
    default:
      return ''
  }
})

const customIconColorClasses = computed<VueCssClass | undefined>(() => {
  if (isDarkMode.value) return 'text-light-text-300'

  switch (currentPage.value) {
    case LandsidePage.Education:
      return 'text-[rgba(3,51,0,0.38)]'
    case LandsidePage.Board:
      return 'text-[rgba(28,0,51,0.38)]'
    case LandsidePage.Sandbox:
      return 'text-[rgba(51,39,0,0.38)]'
    default:
      return ''
  }
})

const ringOffsetColorClasses = computed<VueCssClass | undefined>(() => {
  switch (currentPage.value) {
    case LandsidePage.Home:
      return 'ring-offset-[#82D681]'
    case LandsidePage.Board:
      return 'ring-offset-grape-0 dark:ring-offset-grey-800'
    case LandsidePage.Sandbox:
      return 'ring-offset-canary-0 dark:ring-offset-grey-800'
    case LandsidePage.Education:
      return 'ring-offset-park-0 dark:ring-offset-grey-800'
    default:
      return 'ring-offset-light-ui-200 dark:ring-offset-dark-ui-200'
  }
})

function handleObjectPosition(shouldReverse: boolean): string {
  if (shouldReverse) {
    // For rtl, we need to position the object from the left
    if (dir() === 'ltr') return 'right'
    return 'left'
  }
  if (dir() === 'ltr') return 'left'
  return 'right'
}

const xLandsideCallToAction = computed((): boolean => {
  if (isUserLoggedIn.value) return false
  if (currentPage.value === LandsidePage.Product) return true
  if (currentPage.value === LandsidePage.Subscriptions) return true

  return false
})
</script>

<template>
  <footer :class="['flex flex-col', 'mt-auto']">
    <LandsideCallToAction v-if="xLandsideCallToAction" />
    <LandsideHomepageFooterIllustration v-if="isHomePage" />
    <LandsideHeaderFooterBorder
      v-else-if="xCustomFooterBorder"
      :dark-mode="isDarkMode"
      :flip="currentPage !== LandsidePage.Board && currentPage !== LandsidePage.Sandbox"
    />
    <div
      v-else
      ref="illustrationContainer"
      :class="['flex h-20 tablet-landscape:h-[138px] -mb-0.25', xLandsideCallToAction && 'mt-6 tablet-landscape:mt-24']"
    >
      <ImageThumbnail
        v-for="index in numberOfIllustrationsDisplayed"
        :key="index"
        :style="
          index < numberOfIllustrationsDisplayed
            ? { width: `${illustrationWidth}px` }
            : { width: `${lastIllustrationWidth}px` }
        "
        data-testid="footerIllustrationImage"
        :src="isDarkMode ? footerIllustrationDarkUrl : footerIllustrationLightUrl"
        :original-image-height="ILLUSTRATION_ORIGINAL_IMAGE_HEIGHT"
        :original-image-width="ILLUSTRATION_ORIGINAL_IMAGE_WIDTH"
        :width="illustrationWidth"
        :height="illustrationHeight"
        resize-strategy="fit"
        :process-image="true"
        :object-position="handleObjectPosition(index % 2 === 0)"
        :flip-direction="index % 2 === 0 ? 'horizontal' : undefined"
        :lazy-loading="false"
        aria-hidden="true"
      />
    </div>
    <div
      :class="[
        'flex flex-col',
        'tablet-landscape:items-center',
        {
          'bg-[#82D681]': isHomePage,
          'bg-grape-0 dark:bg-grey-800': isBoardPage,
          'bg-canary-0 dark:bg-grey-800': isSandboxPage,
          'bg-park-0 dark:bg-grey-800': isEducationPage,
          'bg-light-ui-200 dark:bg-dark-ui-200': !isHomePage && !isSandboxPage && !isEducationPage && !isBoardPage,
        },
      ]"
    >
      <!-- On desktop, users navigate from social media icons to the main pages -->
      <div
        v-if="!isSmallerThanTabletLandscape"
        :class="['flex flex-row', 'pt-12 pb-16', 'items-start', 'max-w-320 desktop:w-320']"
      >
        <!-- :Padlet segment -->
        <div :class="['flex flex-col', 'items-start', 'pt-0.75', 'mt-0', 'mb-0', 'w-50 desktop:w-70']">
          <OzPadletLogo
            :dark-mode="isHomePage ? false : undefined"
            :size-preset="OzPadletLogoSizePreset.W86px"
            :x-crane-icon="true"
            :color-scheme="OzPadletLogoColorScheme.Text100"
            :href="landsidePages[0].href"
            class="mb-2"
          />
          <span
            :class="[
              'w-[186px]',
              'mb-3.5',
              'text-body-extra-small',
              'text-start',
              isHomePage ? 'text-dark-text-100' : 'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ __('Oh wow, you’re so beautiful. Just saying!') }}</span
          >
          <nav :aria-label="__('Social media')">
            <ul class="flex mb-4.5">
              <li v-for="(socialMediaIcon, index) in socialMediaIcons" :key="index">
                <OzPlainButton
                  :dark-mode="isHomePage ? false : undefined"
                  :size-preset="OzPlainButtonSizePreset.Bare"
                  :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
                  :href="socialMediaIcon.url"
                  :aria-label="socialMediaIcon.label"
                  :data-testid="socialMediaIcon.testId"
                  class="group w-9 h-9 rounded-xl focus-visible:ring-[2.5px]"
                >
                  <OzIcon
                    :name="socialMediaIcon.icon"
                    :size="socialMediaIcon.size"
                    :class="
                      isHomePage
                        ? [
                            // Base
                            'text-dark-text-300',
                            // Hover
                            'group-hover:text-dark-text-200',
                            // Active
                            'group-active:text-grape-500',
                          ]
                        : [
                            // Base
                            useCustomTextColor ? customIconColorClasses : 'text-dark-text-300 dark:text-light-text-300',
                            // Hover
                            'group-hover:text-dark-text-200 dark:group-hover:text-light-text-200',
                            // Active
                            'group-active:text-grape-500 dark:group-active:text-canary-500',
                          ]
                    "
                  />
                </OzPlainButton>
              </li>
            </ul>
          </nav>
          <div class="flex space-s-2.5 items-center">
            <OzIconButton
              icon-name="accessibility_outline"
              :dark-mode="isHomePage ? false : undefined"
              :label="__('Accessibility')"
              :size-preset="OzIconButtonSizePreset.H32px"
              :icon-size="20"
              :color-scheme="OzIconButtonColorScheme.Bare"
              data-testid="accessibilityButton"
              :ring-offset-color-classes="ringOffsetColorClasses"
              :class="
                isHomePage
                  ? [
                      // Base
                      'bg-button-light',
                      'text-dark-text-200',
                      // Hover
                      'hover-hover:hover:bg-button-hover-light',
                      // Focus
                      'focus-visible:ring-[2.5px] ring-grape-500 ring-offset-[1.5px]',
                    ]
                  : [
                      // Base
                      'bg-button-light dark:bg-button-dark',
                      useCustomTextColor ? customTextColorClasses : 'text-dark-text-200 dark:text-light-text-200',
                      // Hover
                      'hover-hover:hover:bg-button-hover-light hover-hover:dark:hover:bg-button-hover-dark',
                      // Focus
                      'focus-visible:ring-[2.5px] ring-grape-500 dark:ring-canary-500 ring-offset-[1.5px]',
                    ]
              "
              @click="accessibilitySettingsStore.openAccessibilitySettingsModal()"
            />
            <section :aria-label="__('Language selection')">
              <LocaleSelector
                :dark-mode="isHomePage ? false : undefined"
                :should-bold-dropdown-button-text="true"
                :dropdown-button-ring-offset-color-classes="ringOffsetColorClasses"
                :dropdown-button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
              />
            </section>
          </div>
        </div>

        <div :class="['flex flex-1 flex-row', 'justify-around', 'space-s-4', 'ms-4']">
          <!-- Products segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Product')"
            :navigation-items="productNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Solutions segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Solutions')"
            :navigation-items="solutionsNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Support segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Support')"
            :navigation-items="supportNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Company segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Company')"
            :navigation-items="companyNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Legal segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Legal')"
            :x-divider="false"
            :navigation-items="legalNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
        </div>
      </div>
      <!-- On tablet/mobile, users navigate from the main pages to social media icons -->
      <div v-else :class="['flex flex-col', xCustomFooterBorder && 'pt-6']">
        <div :class="['flex flex-1 flex-col']">
          <!-- Products segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Product')"
            :navigation-items="productNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Solutions segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Solutions')"
            :navigation-items="solutionsNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Support segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Support')"
            :navigation-items="supportNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Company segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Company')"
            :navigation-items="companyNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
          <!-- Legal segment -->
          <LandsideFooterSection
            :is-light-mode="isHomePage"
            :title="__('Legal')"
            :x-divider="false"
            :navigation-items="legalNavigationItems"
            class="flex-1"
            :button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
          />
        </div>

        <!-- :Padlet segment -->
        <div :class="['flex flex-col', 'items-center', 'pt-0.75', 'mt-8', 'mb-11']">
          <OzPadletLogo
            :dark-mode="isHomePage ? false : undefined"
            :size-preset="OzPadletLogoSizePreset.W86px"
            :x-crane-icon="true"
            :color-scheme="OzPadletLogoColorScheme.Text100"
            :href="landsidePages[0].href"
            class="mb-2"
          />
          <span
            :class="[
              'w-[186px]',
              'mb-3.5',
              'text-body-extra-small',
              'text-center tablet-landscape:text-start',
              isHomePage ? 'text-dark-text-100' : 'text-dark-text-100 dark:text-light-text-100',
            ]"
          >
            {{ __('Oh wow, you’re so beautiful. Just saying!') }}</span
          >
          <nav :aria-label="__('Social media')">
            <ul class="flex mb-4.5">
              <li v-for="(socialMediaIcon, index) in socialMediaIcons" :key="index">
                <OzPlainButton
                  :dark-mode="isHomePage ? false : undefined"
                  :size-preset="OzPlainButtonSizePreset.Bare"
                  :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
                  :href="socialMediaIcon.url"
                  :aria-label="socialMediaIcon.label"
                  :data-testid="socialMediaIcon.testId"
                  class="group w-9 h-9 rounded-xl focus-visible:ring-[2.5px]"
                >
                  <OzIcon
                    :name="socialMediaIcon.icon"
                    :size="socialMediaIcon.size"
                    :class="
                      isHomePage
                        ? [
                            // Base
                            'text-dark-text-300',
                            // Hover
                            'group-hover:text-dark-text-200',
                            // Active
                            'group-active:text-grape-500',
                          ]
                        : [
                            // Base
                            useCustomTextColor ? customIconColorClasses : 'text-dark-text-300 dark:text-light-text-300',
                            // Hover
                            'group-hover:text-dark-text-200 dark:group-hover:text-light-text-200',
                            // Active
                            'group-active:text-grape-500 dark:group-active:text-canary-500',
                          ]
                    "
                  />
                </OzPlainButton>
              </li>
            </ul>
          </nav>
          <div class="flex space-s-2.5 items-center">
            <OzIconButton
              icon-name="accessibility_outline"
              :dark-mode="isHomePage ? false : undefined"
              :label="__('Accessibility')"
              :size-preset="OzIconButtonSizePreset.H32px"
              :icon-size="20"
              :color-scheme="OzIconButtonColorScheme.Bare"
              data-testid="accessibilityButton"
              :ring-offset-color-classes="ringOffsetColorClasses"
              :class="
                isHomePage
                  ? [
                      // Base
                      'bg-button-light',
                      'text-dark-text-200',
                      // Hover
                      'hover-hover:hover:bg-button-hover-light',
                      // Focus
                      'focus-visible:ring-[2.5px] ring-grape-500 ring-offset-[1.5px]',
                    ]
                  : [
                      // Base
                      'bg-button-light dark:bg-button-dark',
                      useCustomTextColor ? customTextColorClasses : 'text-dark-text-200 dark:text-light-text-200',
                      // Hover
                      'hover-hover:hover:bg-button-hover-light hover-hover:dark:hover:bg-button-hover-dark',
                      // Focus
                      'focus-visible:ring-[2.5px] ring-grape-500 dark:ring-canary-500 ring-offset-[1.5px]',
                    ]
              "
              @click="accessibilitySettingsStore.openAccessibilitySettingsModal()"
            />
            <section :aria-label="__('Language selection')">
              <LocaleSelector
                :dark-mode="isHomePage ? false : undefined"
                :should-bold-dropdown-button-text="true"
                :dropdown-button-ring-offset-color-classes="ringOffsetColorClasses"
                :dropdown-button-text-color-classes="useCustomTextColor ? customTextColorClasses : undefined"
              />
            </section>
          </div>
        </div>
      </div>
    </div>
    <AccessibilitySettingsModal
      :visible="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :portal-selector="'body'"
      :user="currentUser ?? undefined"
      :settings-to-show="[AccessibilitySetting.HighContrastMode, AccessibilitySetting.ReducedMotion]"
      @close="accessibilitySettingsStore.closeAccessibilitySettingsModal"
    />
  </footer>
</template>
